/**
 * v-table-height —— Element Plus 表格高度自适应指令
 *
 * 作用：自动测量表格内部滚动容器（.el-table__body-wrapper）距视口顶的距离，
 *      把「视口剩余高度 − 底部预留」设为它的 max-height，
 *      实现表头固定、表体内部滚动，使列表页内容始终约束在一屏内。
 *
 * 适用：各业务列表页的 <el-table>。查询栏行数、登录角色、窗口尺寸变化均自动适配，
 *      无需在每个页面手写固定高度。
 *
 * 用法：
 *   <el-table v-table-height>          // 使用默认底部预留
 *   <el-table v-table-height="160">    // 自定义底部预留像素（分页器更高 / 有额外脚注时）
 *
 * 注意：依赖 Element Plus 表格内部 DOM 结构（.el-table__body-wrapper），
 *      本项目锁定 element-plus@2.3.6；升级大版本时需复核此指令。
 */

// 默认底部预留：分页器区（约 80）+ 卡片底内边距（约 20）+ 缓冲（约 20）
const DEFAULT_BOTTOM_RESERVE = 120
// 表体最小高度兜底，避免极矮窗口下算出负值
const MIN_BODY_HEIGHT = 150
// resize 防抖间隔（ms）
const RESIZE_DEBOUNCE = 150

function resolveBottomReserve(binding) {
  const reserve = Number(binding.value)
  return Number.isFinite(reserve) && reserve > 0 ? reserve : DEFAULT_BOTTOM_RESERVE
}

function applyTableHeight(tableEl, bottomReserve) {
  const bodyWrapper = tableEl.querySelector('.el-table__body-wrapper')
  if (!bodyWrapper) return
  // 测 body-wrapper 自身的 top（已包含表头高度），减去底部预留即为表体可用高度
  const bodyTop = bodyWrapper.getBoundingClientRect().top
  const available = window.innerHeight - bodyTop - bottomReserve
  bodyWrapper.style.maxHeight = Math.max(Math.floor(available), MIN_BODY_HEIGHT) + 'px'
  bodyWrapper.style.overflowY = 'auto'
}

export default {
  mounted(tableEl, binding) {
    const bottomReserve = resolveBottomReserve(binding)
    // 首次测量：用 rAF 等表头 / 布局渲染稳定后再量
    requestAnimationFrame(() => applyTableHeight(tableEl, bottomReserve))

    // 窗口尺寸变化时防抖重算（缩放、查询栏换行都会改变可用高度）
    let resizeTimer = null
    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => applyTableHeight(tableEl, bottomReserve), RESIZE_DEBOUNCE)
    }
    window.addEventListener('resize', onResize)

    tableEl.tableHeightContext = {
      bottomReserve,
      onResize,
      clearTimer: () => resizeTimer && clearTimeout(resizeTimer),
    }
  },
  updated(tableEl) {
    // 数据换页 / 列变化导致重渲染后，重新测量
    const context = tableEl.tableHeightContext
    if (context) requestAnimationFrame(() => applyTableHeight(tableEl, context.bottomReserve))
  },
  unmounted(tableEl) {
    const context = tableEl.tableHeightContext
    if (context) {
      window.removeEventListener('resize', context.onResize)
      context.clearTimer()
      delete tableEl.tableHeightContext
    }
  },
}
